<template>
  <v-card flat height="auto" v-if="showAll">
    <v-window v-model="step" touchless>
      <v-window-item :value="1">
        <v-form
          ref="formStep1"
          v-model="validStep1"
          v-on:submit.prevent="
            () => {
              return false;
            }
          "
        >
          <advice color="primary" class="my-3" v-if="restrictionAdvice">
            <span
              v-html="
                $t('parking.restriction.advice', {
                  minEntryTime:
                    this.$options.filters.formatTimeRounded(minDate),
                  minEntryDate: this.$options.filters.formatDate(minDate),
                })
              "
            ></span>
          </advice>
          <v-card-title
            class="grey--text grey--lighten-1 body-1 pb-0 mt-3"
            v-if="currentTitle"
          >
            {{ $t(currentTitle) }}
          </v-card-title>

          <v-card-text>
            <reservation-date-time-picker
              :input-date="inputDate"
              :min-date="minDate"
              @on-date-selected="onDateSelected"
              :input-time="inputTime"
              :parking-timezone="parking.timezone"
              @on-time-selected="onTimeSelected"
              ref="reservationDateTimePicker"
            >
            </reservation-date-time-picker>
            <template v-if="parkingHasBle || parkingHasCamara">
              <reservation-plate-selection
                :plates="user.plates"
                :plate-code-selected="bookingData.plate"
                @on-plate-selected="onPlateSelected"
                @on-plate-added="onPlateAdded"
                :key="plateSelectorKey"
              >
              </reservation-plate-selection>
            </template>
            <template v-else-if="parkingHasApr">
              <reservation-tag-selection
                :tags="user.tags"
                :tag-code-selected="bookingData.tagSelected.code"
                :can-edit-plate="false"
                @on-tag-selected="onTagSelected"
                @on-plate-changed="onPlateChanged"
              >
              </reservation-tag-selection>
            </template>
          </v-card-text>

          <advice v-if="hasObservationAsWarning" color="warning" class="my-3">
            <p
              class="font-weight-medium"
              v-html="parking.description.observationWarning.title"
            ></p>
            <p v-html="parking.description.observationWarning.text"></p>

            <v-checkbox
              :label="$t('global.button.agree')"
              class="pa-0"
              :rules="warningRules"
              v-model="acceptWarningMessage"
            ></v-checkbox>
          </advice>
        </v-form>
      </v-window-item>

      <v-window-item :value="2">
        <advice color="warning">
          <p class="ma-0" v-html="message"></p>
        </advice>

        <v-card-text>
          <v-layout row wrap class="text-xs-left pl-2">
            <v-flex class="grey--text text--darken-1 caption" xs12>{{
              $t('global.max.availability')
            }}</v-flex>
            <v-flex>{{ warningMaxAvailability | formatDateAsUtc }}</v-flex>
          </v-layout>
        </v-card-text>

        <v-card-actions class="pl-2">
          <v-checkbox
            :label="$t('global.accept.max.availability')"
            v-model="acceptMaxAvailability"
          ></v-checkbox>
        </v-card-actions>
      </v-window-item>

      <v-window-item :value="100">
        <advice color="warning">
          <p class="title pb-3" v-html="message"></p>
        </advice>

        <h3
          v-if="alternativeParkingList.length > 0"
          class="grey--text grey--lighten-1 body-1 mb-2 px-3 pt-3"
        >
          {{ $t('reservation.failed.alternatives') }}
        </h3>

        <alternative-parkings
          :parkings="alternativeParkingList"
          :date="booking.entryDate"
        ></alternative-parkings>
      </v-window-item>

      <v-window-item :value="200">
        <advice color="success">
          <p class="title pb-3" v-html="$t('reservation.edit.success1')"></p>
          <p
            class="subheading mb-2"
            v-html="
              $t('reservation.success2', {
                entryTime: inputTimeFormatted,
                entryDate: inputDateFormatted,
              })
            "
          ></p>
          <span class="subheading" v-html="$t('reservation.success3')"></span>
          <span
            class="subheading"
            v-html="
              $t('reservation.success4', {
                maxAvailabilityTime: maxAvailabilityTimeFormatted,
                maxAvailabilityDate: maxAvailabilityDateFormatted,
              })
            "
            v-if="acceptMaxAvailability"
          ></span>
        </advice>
      </v-window-item>
    </v-window>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn
        flat
        v-if="step !== 200"
        color="primary"
        round
        class="initialize"
        @click="cancelEdit"
      >
        {{ $t('global.button.cancel') }}</v-btn
      >

      <v-btn
        v-if="step > 1 && step < 200"
        round
        flat
        color="primary"
        class="initialize white--text mr-4"
        @click="goToStep1()"
      >
        {{ backLabel() }}
      </v-btn>

      <v-btn
        :disabled="!valid"
        :loading="isLoading"
        v-if="showBookingButton"
        type="submit"
        color="accent"
        class="capitalize white--text mr-4"
        @click="checkAvailability"
        round
        outline
      >
        {{ $t('global.action.modify') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import _ from 'lodash';
import { EventBus } from '@/services/eventBus';
import AlternativeParkings from '@/components/commons/AlternativeParkings';
import ReservationDateTimePicker from '@/components/commons/ReservationDateTimePicker';
import keys from '@/store/keys';
import manageErrorService from '@/services/manageErrorService';
import moment from 'moment';
import reservationService from '@/services/reservationService';
import ReservationTagSelection from '@/components/commons/ReservationTagSelection';
import ReservationPlateSelection from '@/components/commons/ReservationPlateSelection';
import store from '@/store/';
import teletagService from '@/services/teletagService';
import plateService from '@/services/plateService';
import Advice from '@/components/commons/Advice';

export default {
  name: 'BookingEdit',
  components: {
    [ReservationDateTimePicker.name]: ReservationDateTimePicker,
    [AlternativeParkings.name]: AlternativeParkings,
    [ReservationTagSelection.name]: ReservationTagSelection,
    [ReservationPlateSelection.name]: ReservationPlateSelection,
    [Advice.name]: Advice,
  },
  props: {
    parking: {
      type: Object,
      required: true,
    },
    bookingData: {
      type: Object,
      required: true,
    },
  },
  computed: {
    restrictionAdvice() {
      if (
        this.parking.restrictions &&
        (this.parking.restrictions.notBeforeMinutes > 0 ||
          !this.parking.restrictions.notBeforeIsAbsolute)
      ) {
        let now = moment();
        let restriction = moment();
        if (!this.parking.restrictions.notBeforeIsAbsolute) {
          restriction = restriction.startOf('day');
        }
        restriction = restriction.add(
          this.parking.restrictions.notBeforeMinutes,
          'minutes');
        return restriction.isAfter(now); // true if the restriction is in future
      } else {
        return false;
      }
    },
    parkingHasApr() {
      return this.parking.hasAPR;
    },
    parkingHasBle() {
      return this.parking.hasBLE;
    },
    parkingHasCamara() {
      return this.parking.hasCamara;
    },
    plateRules() {
      return [
        (v) =>
          /^[a-zA-Z0-9-]+$/.test(v) ||
          this.$i18n.t('plate.validation.valid.characters'),
      ];
    },
    valid() {
      if (this.step === 1) {
        return this.validStep1;
      }
      if (this.step === 2) {
        return this.acceptMaxAvailability;
      }
      return true;
    },
    showBookingButton() {
      return this.step <= 2;
    },
    isLogged() {
      return store.state.isLogged;
    },
    maxAvailabilityTimeFormatted() {
      if (this.warningMaxAvailability === null) {
        return '';
      }
      let date = moment.utc(this.warningMaxAvailability);
      return this.$options.filters.formatTime(date);
    },
    maxAvailabilityDateFormatted() {
      if (this.warningMaxAvailability === null) {
        return '';
      }
      let date = moment.utc(this.warningMaxAvailability);
      return this.$options.filters.formatDate(date);
    },
    inputTimeFormatted() {
      let momentDate = this.getBookingEntryDateAsMoment();
      if (momentDate !== null) {
        return this.$options.filters.formatTime(momentDate.toDate());
      }
      return '';
    },
    inputDateFormatted() {
      let momentDate = this.getBookingEntryDateAsMoment();
      if (momentDate !== null) {
        return this.$options.filters.formatDate(momentDate.toDate());
      }
      return '';
    },
    warningRules() {
      return [(v) => v || this.$i18n.t('plate.validation.acceptWarning')];
    },
    hasObservationAsWarning() {
      if (_.isEmpty(this.parking)) {
        return false;
      }
      if (this.parking.description.observationWarning === null) {
        return false;
      }
      return typeof this.parking.description.observationWarning !== 'undefined';
    },
  },
  data() {
    return {
      step: 1,
      isLoading: false,
      showAll: true,
      plates: [],
      minDate: this.calculeInitialDateTime(),
      inputDate: null,
      inputTime: '',
      tagSelected: { plate: '' },
      plateSelected: { alias: '' },
      booking: {
        parking: this.parking,
        entryDate: '',
        entryTime: '',
      },
      user: {
        tags: [],
        plates: [],
      },
      plateSelectorKey: 0,
      currentTitle: 'reservation.step.1',
      validStep1: false,
      alternativeParkingList: [],
      warningMaxAvailability: moment(),
      acceptMaxAvailability: false,
      acceptWarningMessage: false,
      message: '',
      job: null,
    };
  },
  beforeDestroy() {
    this.job.stop();
  },
  async mounted() {
    if (this.isLogged && (this.parking.hasBLE || this.parking.hasCamara)) {
      this.loadPlates();
    } else if (this.isLogged && this.parking.hasAPR) {
      this.loadTacs();
    }

    let entryDateTime = moment.utc(this.bookingData.startLocal);
    this.inputDate = entryDateTime.toDate();
    this.inputTime = entryDateTime.format('HH:mm');

    this.booking.entryDate = entryDateTime.format('YYYY-MM-DD');
    this.booking.entryTime = this.inputTime;

    var CronJob = require('cron').CronJob;
    this.job = new CronJob(
      '*/1 * * * *',
      function () {
        this.minDate = this.calculeInitialDateTime();
      },
      null,
      false,
      this.parking.timezone,
      this
    );
    this.job.start();
  },
  methods: {
    calculeInitialDateTime() {
      let initial = moment();
      if (this.parking.restrictions) {
        if (this.parking.restrictions.notBeforeMinutes > 0) {
          let aux = _.cloneDeep(initial)
          if (!this.parking.restrictions.notBeforeIsAbsolute) {
            aux = aux.startOf('day');
          }
          aux = aux.add(
            this.parking.restrictions.notBeforeMinutes,
            'minutes'
          );
          if (aux.isAfter(initial)) {
            initial = aux;
          }
        }
      }

      let minutes = initial.minutes();
      let hours = initial.hours();
      if (minutes === 0) {
        minutes = 0;
      } else if (minutes <= 5) {
        minutes = 5;
      } else if (minutes <= 10) {
        minutes = 10;
      } else if (minutes <= 15) {
        minutes = 15;
      } else if (minutes <= 20) {
        minutes = 20;
      } else if (minutes <= 25) {
        minutes = 25;
      } else if (minutes <= 30) {
        minutes = 30;
      } else if (minutes <= 35) {
        minutes = 35;
      } else if (minutes <= 40) {
        minutes = 40;
      } else if (minutes <= 45) {
        minutes = 45;
      } else if (minutes <= 50) {
        minutes = 50;
      } else if (minutes <= 55) {
        minutes = 55;
      } else {
        minutes = 0;
        if (hours === 23) {
          hours = 0;
          this.form.day = moment().add(1, 'day').format('YYYY-MM-DD');
        } else {
          hours++;
        }
      }
      initial = initial
        .set('hours', hours)
        .set('minutes', minutes)
        .set('seconds', 0)
        .set('milliseconds', 0);

      return initial.toDate();
    },
    getExternalNearestTime(arg) {
      return this.$refs.reservationDateTimePicker.getExternalNearestTime(arg);
    },
    onPlateSelected(params) {
      this.plateSelected = params.plate;
    },
    onPlateAdded(params) {
      this.loadPlates();
    },
    onTagSelected(params) {
      this.tagSelected = params.tag;
    },
    onPlateChanged(params) {
      this.tagSelected.plate = params.plate;
    },
    onDateSelected(params) {
      if (params.date !== this.booking.entryDate) {
        this.booking.entryDate = params.date;
        if (this.booking.entryDate && this.booking.entryTime) {
          this.inputDate = moment(
            this.booking.entryDate + ' ' + this.booking.entryTime
          ).toDate();
        }
      }
    },
    onTimeSelected(params) {
      if (params.timeSelected !== this.booking.entryTime) {
        this.booking.entryTime = params.timeSelected;
        if (this.booking.entryDate && this.booking.entryTime) {
          this.inputDate = moment(
            this.booking.entryDate + ' ' + this.booking.entryTime
          ).toDate();
        }
      }
    },
    async loadPlates() {
      this.plateSelectorKey++;
      let results = await plateService.list({});

      if (results.error) {
        manageErrorService.handle(results.data);
        this.isLoading = false;
        this.valid = true;
        return;
      }

      let data = results.data;
      this.user.plates = data;
      if (data.length > 0) {
        this.plateSelected = data[0];
      }
    },
    async loadTacs() {
      let results = await teletagService.list({});

      if (results.error) {
        manageErrorService.handle(results.data);
        this.isLoading = false;
        this.valid = true;
        return;
      }

      let data = results.data;
      this.user.tags = data;
    },
    async checkAvailability() {
      if (this.userHasErrors()) {
        EventBus.$emit(keys.hideBookingTabs, {
          hidden: true,
        });
        this.showAll = false;

        return;
      }
      this.isLoading = true;

      EventBus.$emit(keys.hideBookingTabs, {
        hidden: true,
      });

      let entryDateLocal = this.getBookingEntryDateAsMoment();
      let params = {
        start: entryDateLocal.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
        reservationId: this.bookingData.id,
      };

      if (this.parkingHasApr) {
        params.teletagId = this.tagSelected.id;
        params.plate = this.tagSelected.plate;
      }

      let result = await reservationService.overlapped(params);

      if (result.error) {
        this.currentTitle = '';
        this.message = result.data.error;
        this.alternativeParkingList = result.data.alternatives;
        this.step = 100;
        this.isLoading = false;
        return;
      }

      if (result.data.shortMaxAvailability) {
        if (
          !this.acceptMaxAvailability ||
          this.warningMaxAvailability !== result.data.maxAvailabilityLocal
        ) {
          this.currentTitle = '';
          this.message = this.$i18n.t('reservation.warning.max.availability');
          this.warningMaxAvailability = result.data.maxAvailabilityLocal;
          this.step = 2;
          this.isLoading = false;
          return;
        }
      }

      let reservationResult = await reservationService.edit(params);

      if (reservationResult.error) {
        manageErrorService.handle(reservationResult.data);
        this.isLoading = false;
        return;
      }
      this.currentTitle = result.data.message;
      this.step = 200;
      this.isLoading = false;
      this.$mam.actionBooking();
    },
    getBookingEntryDateAsMoment() {
      if (
        _.isEmpty(this.booking.entryDate) ||
        _.isEmpty(this.booking.entryTime)
      ) {
        return null;
      }
      let selectedDate = moment(this.booking.entryDate).toDate();
      let hoursMinutes = this.booking.entryTime.split(':');
      return moment([
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        hoursMinutes[0],
        hoursMinutes[1],
      ]);
    },
    userHasErrors() {
      let user = store.state.user;
      let hasRoleBuyBalance = _.find(
        store.state.user.roles,
        (it) => it.authority === 'ROLE_BUY_BALANCE'
      );
      if (
        !this.parking.hasBLE &&
        !this.parking.hasCamara &&
        !this.parking.isFacility
      ) {
        if (this.parking.hasAPR && !user.activeTags) {
          this.$emit('on-user-error', { error: 'showErrorNoTags' });
          return true;
        }
      } else if (user.status !== 'ACTIVED') {
        this.$emit('on-user-error', { error: 'showErrorNotActived' });
        return true;
      } else if (this.parking.isFree) {
        if (user.balance.total < 0 && hasRoleBuyBalance) {
          this.$emit('on-user-error', { error: 'showErrorNoBalance' });
          return true;
        }
      } else if (!this.parking.isFree) {
        if (user.balance.total <= 0 && hasRoleBuyBalance) {
          this.$emit('on-user-error', { error: 'showErrorNoBalance' });
          return true;
        }
      }
      return false;
    },
    goToStep1() {
      this.message = null;
      this.acceptMaxAvailability = false;
      this.acceptWarningMessage = false;
      EventBus.$emit(keys.hideBookingTabs, {
        hidden: false,
      });
      this.currentTitle = 'reservation.step.1';
      this.step = 1;
    },
    goToCalendar() {
      store.commit('SET_RESTORE_HOME_DATA', { data: false });
      store.commit('CHANGE_SHOWING_PARKING_AS_LIST', { showAsList: false });

      this.$router.push({ path: this.$i18n.t('urlMyReservations') });
    },
    backLabel() {
      if (this.step === 200) {
        return this.$i18n.t('reservation.button.doAnother');
      } else {
        return this.$i18n.t('reservation.button.tryAgain');
      }
    },
    cancelEdit() {
      EventBus.$emit(keys.closeReservationDetail);
    },
  },
};
</script>

<style>
.v-text-field--outline.v-text-field--single-line input {
  margin-top: 10px !important;
}
</style>
